import useInterval from 'beautiful-react-hooks/useInterval';
import React from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import config from '../../../../../../config';
import useUsableIdToken from '../../../Hooks/useUsableIdToken';
import { isOKMessage, SessionWebhookResponse } from './types';
import { WebsocketApiConnectionContext } from './useWebsocketApiConnectionContext';

export default function WebsocketApiConnectionContextProvider({ children }: { children: React.ReactNode }) {
  // See useUsableIdToken: an expired token must not reach the handshake, because the
  // browser cannot see the resulting 401 and would retry it indefinitely.
  const { idToken, isUsable: isIdTokenUsable, requestRefresh } = useUsableIdToken();
  const {
    sendJsonMessage, readyState, lastJsonMessage,
  } = useWebSocket<SessionWebhookResponse>(config.Live.WebSocketUrl, {
    reconnectAttempts: Infinity,
    shouldReconnect: () => isIdTokenUsable,
    reconnectInterval(lastAttemptNumber) {
      if (lastAttemptNumber < 10) {
        return 500;
      }
      return Math.min(2 ** (lastAttemptNumber - 10) * 1000, 60000);
    },
    onError: () => {
      console.error('Web socket connection error');
      requestRefresh();
    },
    protocols: ['cogneed-ws-protocol', ...idToken ? [idToken] : []],
  }, isIdTokenUsable);

  useInterval(() => {
    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({ type: 'ping' });
    }
  }, 60000);

  React.useEffect(() => {
    if (isOKMessage(lastJsonMessage)) {
      sendJsonMessage({ type: 'ActionTimestamp', message: 'WebNavigation' });
    }
  }, [lastJsonMessage]);

  const value = React.useMemo(() => ({
    sendJsonMessage,
    lastJsonMessage,
    readyState,
  }), [sendJsonMessage, lastJsonMessage, readyState]);

  return (
    <WebsocketApiConnectionContext.Provider value={value}>
      {children}
    </WebsocketApiConnectionContext.Provider>
  );
}
